import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { appLogout, injectSlice } from '@bonanza-social/toolkit/state';
import { SessionHistoryState } from './sessionHistory.types';
import { FetchHistoryParams } from '../shared/history.types';
import { isAfterTimestamp } from '@bonanza-social/toolkit/utils';
import { DEFAULT_PAGE_SIZE } from '../shared/history.constants';
import { AccountService } from '../../api';

export const fetchSessionHistory = createAsyncThunk(
  'sessionHistory/fetch',
  async ({ startDate, pageNumber = 1, replace }: FetchHistoryParams) => {
    const response = await AccountService.getSessionHistory({
      params: {
        pageNumber: replace ? 1 : pageNumber,
        loginDateFrom: startDate,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    return response;
  },
);

const initialState: SessionHistoryState = {
  data: undefined,
  dataState: 'idle',
  lastTimestamp: undefined,
};

const sessionHistory = createSlice({
  name: 'sessionHistory',
  initialState,
  reducers: {
    resetSessionLastTimestamp(state) {
      state.lastTimestamp = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSessionHistory.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchSessionHistory.fulfilled, (state, action) => {
      const { replace, startDate } = action.meta.arg;
      state.dataState = 'fulfilled';
      state.lastTimestamp = startDate;
      if (replace) {
        state.data = action.payload;
      } else {
        const oldItems = state.data?.items;
        const newItems = action.payload.items;
        state.data = action.payload;
        if (oldItems) {
          state.data.items = [...oldItems, ...newItems];
        }
      }
    });
    builder.addCase(fetchSessionHistory.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const withSessionHistory = injectSlice({
  reducerPath: 'sessionHistory',
  reducer: sessionHistory.reducer,
});

const selectAllItems = withSessionHistory.selector(
  (state) => state.sessionHistory.data?.items,
);

export const selectSessionHistoryLastTimestamp = withSessionHistory.selector(
  (state) => state.sessionHistory.lastTimestamp,
);

export const selectSessionHistoryItems = createSelector(
  [selectAllItems, (_, timestamp) => timestamp],
  (allItems, timestamp) =>
    (allItems ?? []).filter((item) =>
      isAfterTimestamp(item.loginDateTime, timestamp),
    ),
);

export const selectSessionHistoryDataState = withSessionHistory.selector(
  (state) => state.sessionHistory.dataState,
);

export const selectSessionHistoryHasNextPage = withSessionHistory.selector(
  (state) => state.sessionHistory.data?.hasNextPage,
);

export const selectSessionHistoryPageNumber = withSessionHistory.selector(
  (state) => state.sessionHistory.data?.pageNumber,
);

export const { resetSessionLastTimestamp } = sessionHistory.actions;

export default sessionHistory.reducer;

declare global {
  interface LazyLoadedSlices {
    sessionHistory: ReturnType<typeof sessionHistory.reducer>;
  }
}
